(() => {
  'use strict';

  const motionAllowed = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Drawers: the cart and the phone menu share one dialog pattern. */
  const openers = new WeakMap();

  /* Controls that point at a drawer and declare aria-expanded (the menu button) mirror its open state.
     Runs on open and on the dialog's close event, which every close path ends in. */
  const syncExpanded = (dialog) => {
    if (!dialog.id) return;
    document.querySelectorAll(`[aria-controls~="${CSS.escape(dialog.id)}"][aria-expanded]`).forEach((control) => {
      control.setAttribute('aria-expanded', String(dialog.open));
    });
  };

  const openDrawer = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    openers.set(dialog, opener || document.activeElement);
    dialog.classList.remove('is-closing');
    dialog.showModal();
    syncExpanded(dialog);
    const close = dialog.querySelector('[data-sk-close]');
    if (close) close.focus();
  };

  const closeDrawer = (dialog, instant = false) => {
    if (!dialog || !dialog.open) return;
    if (instant || !motionAllowed()) {
      dialog.classList.remove('is-closing');
      dialog.close();
      return;
    }
    if (dialog.classList.contains('is-closing')) return;
    dialog.classList.add('is-closing');
    // The animation's end or the fallback timer, whichever comes first, closes the drawer. A drawer that
    // was opened again in between has lost the class, so the later one leaves it open.
    const finish = () => {
      if (!dialog.classList.contains('is-closing')) return;
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    dialog.addEventListener('animationend', finish, { once: true });
    window.setTimeout(finish, 450);
  };

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('sk-drawer')) return;
      syncExpanded(dialog);
      const opener = openers.get(dialog);
      openers.delete(dialog);
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    },
    true
  );

  document.addEventListener(
    'cancel',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('sk-drawer')) return;
      event.preventDefault();
      closeDrawer(dialog);
    },
    true
  );

  /* Cart: drawer and cart page re-render through the Section Rendering API. */
  const cart = {
    get dialog() {
      return document.getElementById('SkCartDrawer');
    },

    pages() {
      return document.querySelectorAll('[data-sk-cart-page]');
    },

    canUseDrawer() {
      return Boolean(this.dialog) && this.pages().length === 0;
    },

    open(opener) {
      openDrawer(this.dialog, opener);
    },

    text(key) {
      return (this.dialog && this.dialog.dataset[key]) || '';
    },

    sectionIds() {
      const ids = this.dialog ? ['sk-cart-drawer'] : [];
      this.pages().forEach((page) => ids.push(page.dataset.sectionId));
      return ids;
    },

    url(kind) {
      const fallback = { add: '/cart/add', change: '/cart/change' };
      const fromDialog = this.dialog && this.dialog.dataset[`${kind}Url`];
      return `${fromDialog || fallback[kind]}.js`;
    },

    async post(kind, payload) {
      const body = Object.assign({}, payload, {
        sections: this.sectionIds().join(','),
        sections_url: window.location.pathname,
      });
      const response = await fetch(this.url(kind), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(data.description || data.message || response.statusText);
        error.status = response.status;
        throw error;
      }
      return data;
    },

    setBusy(busy) {
      const targets = [this.dialog, ...this.pages()].filter(Boolean);
      targets.forEach((target) => target.setAttribute('aria-busy', String(busy)));
    },

    announce(message) {
      const status = this.dialog && this.dialog.querySelector('[data-sk-cart-status]');
      if (!status || !message) return;
      status.textContent = '';
      window.setTimeout(() => {
        status.textContent = message;
      }, 60);
    },

    updateCount(count) {
      if (!Number.isFinite(count)) return;
      document.querySelectorAll('[data-sk-cart-count]').forEach((badge) => {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      });
      document.querySelectorAll('[data-sk-cart-link]').forEach((link) => {
        const template = count === 1 ? link.dataset.labelOne : link.dataset.labelOther;
        if (template) link.setAttribute('aria-label', template.replace('[count]', String(count)));
      });
    },

    render(sections, focusKey) {
      if (!sections) return;
      const parser = new DOMParser();
      const drawerHtml = sections['sk-cart-drawer'];
      if (drawerHtml && this.dialog) {
        const fresh = parser.parseFromString(drawerHtml, 'text/html').querySelector('[data-sk-cart-content]');
        const current = this.dialog.querySelector('[data-sk-cart-content]');
        if (fresh && current) {
          current.replaceWith(fresh);
          this.updateCount(Number(fresh.dataset.count));
          initOffers(fresh);
          initCountdowns();
        }
      }
      this.pages().forEach((page) => {
        const html = sections[page.dataset.sectionId];
        if (!html) return;
        const fresh = parser.parseFromString(html, 'text/html').querySelector('[data-sk-cart-page]');
        if (!fresh) return;
        page.replaceWith(fresh);
        this.updateCount(Number(fresh.dataset.count));
        initOffers(fresh);
      });
      if (focusKey) this.restoreFocus(focusKey);
    },

    restoreFocus(focusKey) {
      const scope = this.dialog && this.dialog.open ? this.dialog : document.querySelector('[data-sk-cart-page]');
      if (!scope) return;
      const target =
        scope.querySelector(`[data-sk-focus="${CSS.escape(focusKey)}"]`) ||
        scope.querySelector('[data-sk-focus="close"]') ||
        scope.querySelector('h1, h2');
      if (!target) return;
      if (!target.matches('a, button, input, select, textarea')) target.setAttribute('tabindex', '-1');
      target.focus();
    },

    showError(error) {
      const message = error && error.status === 422 && error.message ? error.message : this.text('errorText');
      this.announce(message);
      const scope = this.dialog && this.dialog.open ? this.dialog : document.querySelector('[data-sk-cart-page]');
      const body = scope && scope.querySelector('.sk-cart__body, .sk-cart__lines');
      if (!body) return;
      let alert = scope.querySelector('[data-sk-cart-error]');
      if (!alert) {
        alert = document.createElement('p');
        alert.className = 'sk-cart__error';
        alert.setAttribute('role', 'alert');
        alert.dataset.skCartError = '';
        body.prepend(alert);
      }
      alert.textContent = message;
    },

    async change(id, quantity, focusKey) {
      this.setBusy(true);
      try {
        const data = await this.post('change', { id, quantity });
        this.render(data.sections, focusKey);
        this.announce(this.text('updatedText'));
      } catch (error) {
        this.showError(error);
      } finally {
        this.setBusy(false);
      }
    },

    async add(items, opener) {
      const data = await this.post('add', { items });
      this.render(data.sections);
      if (this.canUseDrawer()) {
        this.open(opener);
      } else if (this.pages().length === 0) {
        window.location.href = (this.dialog && this.dialog.dataset.cartUrl) || '/cart';
        return;
      }
      this.announce(this.text('addedText'));
    },

    async refresh() {
      const ids = this.sectionIds();
      if (ids.length === 0) return;
      try {
        const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`, {
          headers: { Accept: 'application/json' },
        });
        if (response.ok) this.render(await response.json());
      } catch (error) {
        /* Keep the server-rendered cart if the refresh fails. */
      }
    },
  };

  const upgradeCartLinks = () => {
    if (!cart.canUseDrawer()) return;
    document.querySelectorAll('[data-sk-cart-link]').forEach((link) => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'SkCartDrawer');
    });
  };

  /* Pack links ("?pack=2#buy"): on the page of the same product they pick that bundle in place,
     scroll to the buy box and move focus to the chosen bundle. Anywhere else they simply navigate. */
  const choosePack = (link) => {
    const product = document.querySelector('[data-sk-product]');
    const n = Number(link.dataset.skPackLink);
    if (!product || !n) return false;
    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return false;
    }
    if (url.pathname.split('/').filter(Boolean).pop() !== product.dataset.handle) return false;
    const input = Array.from(product.querySelectorAll('[data-sk-tier]')).find((item) => Number(item.value) === n);
    if (!input) return false;

    const dialog = link.closest('dialog');
    if (dialog) {
      openers.delete(dialog);
      closeDrawer(dialog, true);
    }
    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const next = new URL(window.location.href);
    next.searchParams.set('pack', String(n));
    next.hash = url.hash;
    window.history.replaceState(window.history.state, '', next);
    (document.getElementById('buy') || product).scrollIntoView({ behavior: motionAllowed() ? 'smooth' : 'auto', block: 'start' });
    input.focus({ preventScroll: true });
    return true;
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const packLink = target.closest('[data-sk-pack-link]');
    if (packLink && event.button === 0 && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
      if (choosePack(packLink)) {
        event.preventDefault();
        return;
      }
    }

    const opener = target.closest('[data-sk-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(document.getElementById(opener.dataset.skOpen), opener);
      return;
    }

    const cartLink = target.closest('[data-sk-cart-link]');
    if (cartLink && cart.canUseDrawer()) {
      event.preventDefault();
      cart.open(cartLink);
      return;
    }

    const closer = target.closest('[data-sk-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('dialog'));
      return;
    }

    const menuLink = target.closest('[data-sk-close-on-click]');
    if (menuLink) {
      closeDrawer(menuLink.closest('dialog'), true);
      return;
    }

    if (target instanceof HTMLDialogElement && target.classList.contains('sk-drawer')) {
      const box = target.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) closeDrawer(target);
    }
  });

  const focusableIn = (container) =>
    Array.from(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.getClientRects().length > 0);

  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (event.key === ' ' && target instanceof HTMLAnchorElement && target.getAttribute('role') === 'button') {
      event.preventDefault();
      target.click();
      return;
    }

    if (event.key !== 'Tab') return;
    const dialog = document.querySelector('dialog.sk-drawer[open]');
    if (!dialog) return;
    const focusable = focusableIn(dialog);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-sk-line-form]')) {
      event.preventDefault();
      const submitter = event.submitter;
      const quantity = Number(submitter ? submitter.value : form.elements.quantity && form.elements.quantity.value);
      const id = form.elements.id.value;
      cart.change(id, Number.isFinite(quantity) ? quantity : 0, submitter ? submitter.dataset.skFocus : '');
      return;
    }

    if (form.matches('[data-sk-add-form]')) {
      event.preventDefault();
      const data = new FormData(form);
      const button = event.submitter;
      if (button) button.setAttribute('aria-busy', 'true');
      cart
        .add([{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }], button)
        .then(() => cart.restoreFocus('nudge'))
        .catch((error) => cart.showError(error))
        .finally(() => button && button.removeAttribute('aria-busy'));
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) cart.refresh();
  });

  /* Money: format cents like the store's money filter, learned from a rendered sample such as "$1,234.56". */
  const moneyFormatter = (sample) => {
    const match = String(sample || '').match(/\d[\d.,'\s]*\d/);
    if (!match) return (cents) => (cents / 100).toFixed(2);
    const number = match[0];
    const decimalsMatch = number.match(/[.,](\d{1,2})$/);
    const decimals = decimalsMatch ? decimalsMatch[1].length : 0;
    const decimalSeparator = decimals ? number.charAt(number.length - decimals - 1) : '';
    const integerPart = decimals ? number.slice(0, -(decimals + 1)) : number;
    const groupSeparator = integerPart.replace(/\d/g, '').charAt(0);
    const prefix = sample.slice(0, match.index);
    const suffix = sample.slice(match.index + number.length);
    return (cents) => {
      const [whole, fraction] = (Math.round(cents) / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
      return prefix + grouped + (decimals ? decimalSeparator + fraction : '') + suffix;
    };
  };

  /* JSON islands carry HTML-escaped text (the t filter escapes translations, money formats can hold
     entities) and sk.js writes it with textContent, so every string is decoded once. A detached textarea
     decodes character references only: markup inside stays text and nothing is fetched or run. */
  const decoder = document.createElement('textarea');
  const decodeStrings = (value) => {
    if (typeof value === 'string') {
      decoder.innerHTML = value;
      return decoder.value;
    }
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        value[key] = decodeStrings(value[key]);
      });
    }
    return value;
  };

  const readJson = (script) => {
    if (!script) return null;
    try {
      return decodeStrings(JSON.parse(script.textContent));
    } catch (error) {
      return null;
    }
  };

  const readConfig = (container) => readJson(container.querySelector('script[data-sk-bundle-config]'));

  /* Bundle prices: price x N minus the bundle saving. The product page gives a discount % per quantity
     (config.percents), which works in any currency. Elsewhere the savings are store-currency amounts for 2 and 3
     (config.deals), so outside the store currency they are converted with the storefront's rate (the cart always
     shows Shopify's own figure). */
  const bundleTiers = (config) => {
    let rate = 1;
    if (!config.sameCurrency) {
      rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 0;
    }
    const percents = config.percents || null;
    const deals = config.deals || {};
    const counts = new Set([1, ...Object.keys(percents || deals).map(Number)]);
    const tiers = {};
    counts.forEach((n) => {
      const was = config.price * n;
      const off = percents
        ? Math.round((was * (Number(percents[n]) || 0)) / 100)
        : Math.round((Number(deals[n]) || 0) * rate);
      const total = Math.max(0, was - off);
      tiers[n] = { was, total, each: Math.round(total / n), saving: was - total };
    });
    return tiers;
  };

  const setText = (element, text) => {
    if (!element) return;
    element.textContent = text;
    element.removeAttribute('data-sk-pending');
  };

  const initOffers = (root) => {
    root.querySelectorAll('[data-sk-offer]').forEach((offer) => {
      const config = readConfig(offer);
      if (!config || config.sameCurrency || offer.dataset.skReady) return;
      offer.dataset.skReady = 'true';
      const format = moneyFormatter(config.moneySample);
      const tiers = bundleTiers(config);
      offer.querySelectorAll('[data-sk-offer-total]').forEach((element) => {
        setText(element, format(tiers[Number(element.dataset.skOfferTotal)].total));
      });
      offer.querySelectorAll('[data-sk-offer-each]').forEach((element) => {
        const tier = tiers[Number(element.dataset.skOfferEach)];
        const parts = [config.eachText.replace('[price]', format(tier.each))];
        if (tier.saving > 0) parts.push(config.saveText.replace('[amount]', format(tier.saving)));
        setText(element, parts.join(' · '));
      });
      offer.querySelectorAll('[data-sk-offer-saving]').forEach((element) => {
        const tier = tiers[Number(element.dataset.skOfferSaving)];
        if (tier) setText(element, format(tier.saving));
      });
    });
  };

  const initProducts = (root) => {
    root.querySelectorAll('[data-sk-product]').forEach((product) => {
      if (product.dataset.skReady) return;
      const config = readConfig(product);
      if (!config) return;
      product.dataset.skReady = 'true';

      // Without JavaScript the photo row is a focusable region so keyboards can scroll it. This gallery
      // shows one photo at a time, so the row goes back to a plain container.
      const photoRow = product.querySelector('[data-sk-photo-row]');
      if (photoRow) ['role', 'aria-label', 'tabindex'].forEach((name) => photoRow.removeAttribute(name));

      const format = moneyFormatter(config.moneySample);
      const tiers = bundleTiers(config);
      const form = document.getElementById(product.dataset.formId);
      const atc = product.querySelector('[data-sk-atc]');
      const atcLabel = product.querySelector('[data-sk-atc-label]');
      const errorBox = product.querySelector('[data-sk-atc-error]');
      const tierInputs = Array.from(product.querySelectorAll('[data-sk-tier]'));
      const rows = Array.from(product.querySelectorAll('[data-sk-row]'));
      const section = product.closest('.shopify-section') || document;
      const sticky = section.querySelector('[data-sk-sticky]');
      const stickySummary = sticky && sticky.querySelector('[data-sk-sticky-summary]');

      const selectedPack = () => {
        const checked = tierInputs.find((input) => input.checked);
        return checked ? Number(checked.value) : 1;
      };
      const tierName = (n) => {
        const input = tierInputs.find((item) => Number(item.value) === n);
        return input ? input.dataset.name : String(n);
      };
      const eachText = (n) => config.eachText.replace('[price]', format(tiers[n].each));

      if (!config.sameCurrency) {
        tierInputs.forEach((input) => {
          const n = Number(input.value);
          const label = input.closest('.sk-tier');
          setText(label.querySelector('[data-sk-tier-total]'), format(tiers[n].total));
          if (n === 1) return;
          setText(label.querySelector('[data-sk-tier-each]'), eachText(n));
          const saving = label.querySelector('[data-sk-tier-saving]');
          if (saving) {
            setText(saving, format(tiers[n].saving));
            const note = saving.closest('[data-sk-tier-note]');
            if (note && tiers[n].saving <= 0) note.hidden = true;
          }
          const priceBox = label.querySelector('.sk-tier__price');
          if (tiers[n].saving > 0 && priceBox && !priceBox.querySelector('s')) {
            // Same markup as the server prints in the store currency, label included for screen readers.
            const was = document.createElement('s');
            was.dataset.skTierWas = String(n);
            if (config.regularText) {
              const hidden = document.createElement('span');
              hidden.className = 'sk-visually-hidden';
              hidden.textContent = config.regularText;
              was.append(hidden);
            }
            was.append(format(tiers[n].was));
            priceBox.append(was);
          }
        });
      }

      const update = () => {
        const n = selectedPack();
        rows.forEach((row) => {
          row.hidden = Number(row.dataset.skRow) > n;
        });
        setText(product.querySelector('[data-sk-total]'), format(tiers[n].total));
        // "$24.98 each · free shipping"; one item has no "each", so its bundle note leads instead.
        setText(
          product.querySelector('[data-sk-each]'),
          [n > 1 ? eachText(n) : config.tier1Note, config.freeShipping].filter(Boolean).join(' · ')
        );
        setText(atcLabel, config.atcText.replace('[price]', format(tiers[n].total)));
        setText(stickySummary, config.summaryText.replace('[name]', tierName(n)).replace('[price]', format(tiers[n].total)));
      };

      const showSlide = (mediaId) => {
        const slides = product.querySelectorAll('[data-sk-slide]');
        const target = Array.from(slides).find((slide) => slide.dataset.skSlide === String(mediaId));
        if (!target) return false;
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === target));
        product.querySelectorAll('[data-sk-thumb]').forEach((thumb) => {
          const on = thumb.dataset.skThumb === String(mediaId);
          thumb.setAttribute('aria-pressed', String(on));
          if (!on) return;
          // Keep the matching thumbnail visible inside its own strip, without scrolling the page.
          const strip = thumb.parentElement.getBoundingClientRect();
          const box = thumb.getBoundingClientRect();
          thumb.parentElement.scrollLeft += box.left - strip.left - (strip.width - box.width) / 2;
          thumb.parentElement.scrollTop += box.top - strip.top - (strip.height - box.height) / 2;
        });
        return true;
      };

      /* "Showing: <colour>" names the colour only while the main photo is that colour's own photo. */
      const chip = product.querySelector('[data-sk-chip]');
      const setChip = (input) => {
        if (!chip) return;
        chip.hidden = !input;
        if (!input) return;
        const text = chip.querySelector('[data-sk-chip-text]');
        if (text) text.textContent = (config.showingText || '[colour]').replace('[colour]', input.dataset.colour);
        const dot = chip.querySelector('.sk-dot');
        if (dot && input.dataset.swatch) dot.style.setProperty('--swatch', input.dataset.swatch);
        else if (dot) dot.style.removeProperty('--swatch');
      };
      const colourOf = (mediaId) => product.querySelector(`[data-colour][data-media-id="${mediaId}"]`);

      rows.forEach((row) => {
        const checked = row.querySelector('input[type="radio"]:checked');
        const name = row.querySelector('[data-sk-colour-name]');
        if (checked && name) name.textContent = checked.dataset.colour;
      });

      const warmSlide = (mediaId) => {
        const image = product.querySelector(`[data-sk-slide="${mediaId}"] img[loading="lazy"]`);
        if (image) image.loading = 'eager';
      };

      const pack = Number(new URLSearchParams(window.location.search).get('pack'));
      const requested = tierInputs.find((input) => Number(input.value) === pack);
      if (requested) requested.checked = true;
      update();

      product.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('[data-sk-tier]')) update();
        if (target.matches('[data-colour]')) {
          const row = target.closest('[data-sk-row]');
          const name = row && row.querySelector('[data-sk-colour-name]');
          if (name) name.textContent = target.dataset.colour;
          setChip(target.dataset.mediaId && showSlide(target.dataset.mediaId) ? target : null);
        }
      });

      product.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-sk-thumb]');
        if (thumb && showSlide(thumb.dataset.skThumb)) setChip(colourOf(thumb.dataset.skThumb));
      });

      ['pointerover', 'focusin'].forEach((type) => {
        product.addEventListener(type, (event) => {
          const thumb = event.target.closest && event.target.closest('[data-sk-thumb]');
          if (thumb) warmSlide(thumb.dataset.skThumb);
        });
      });

      const setBusy = (busy) => {
        [atc, sticky && sticky.querySelector('button')].filter(Boolean).forEach((button) => {
          button.disabled = busy;
          if (busy) button.setAttribute('aria-busy', 'true');
          else button.removeAttribute('aria-busy');
        });
      };

      if (form && tierInputs.length) {
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          const quantities = new Map();
          rows.slice(0, selectedPack()).forEach((row) => {
            const checked = row.querySelector('input[type="radio"]:checked');
            if (checked) quantities.set(checked.value, (quantities.get(checked.value) || 0) + 1);
          });
          const items = Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
          if (items.length === 0) return;
          const opener = event.submitter || atc;
          setBusy(true);
          if (errorBox) errorBox.hidden = true;
          let failure = null;
          try {
            await window.Satinknit.cart.add(items, opener);
          } catch (error) {
            failure = error || {};
          }
          setBusy(false);
          if (!failure || !errorBox) return;
          errorBox.textContent = (failure.status === 422 && failure.message) || config.errorText;
          errorBox.hidden = false;
          // From the phone bar the message sits above the screen, so it scrolls into view; the bar then hides
          // as the form's own button comes back. Focus, lost while the buttons were disabled, goes to that button.
          const fromBar = Boolean(opener && opener !== atc && opener.closest('[data-sk-sticky]'));
          if (fromBar) errorBox.scrollIntoView({ block: 'center', behavior: motionAllowed() ? 'smooth' : 'auto' });
          const active = document.activeElement;
          if (atc && (fromBar || !active || active === document.body)) atc.focus({ preventScroll: true });
        });
      }

      if (sticky && atc && 'IntersectionObserver' in window) {
        // The root reaches far below the viewport, so the button only stops intersecting once it is
        // above the top edge, even when the page jumps past it (anchor links, fast flings).
        const observer = new IntersectionObserver(
          ([entry]) => {
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            sticky.classList.toggle('is-visible', show);
            document.documentElement.classList.toggle('sk-sticky-on', show);
          },
          { rootMargin: '0px 0px 100000px 0px' }
        );
        observer.observe(atc);
        product.skObserver = observer;
      }
    });
  };

  /* Delivery estimate: today plus N business days (Monday to Friday), in the shopper's time zone and
     language. data-sk-delivery holds the ship days and the delivery days range. No times, no countdown.
     Each date slot is filled on its own: with a single transit time the line carries only the "to" slot. */
  const addBusinessDays = (start, days) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    for (let left = days; left > 0; ) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() % 6 !== 0) left -= 1;
    }
    return date;
  };

  const dayLabel = (date) => {
    const options = { month: 'short', day: 'numeric' };
    try {
      return date.toLocaleDateString(document.documentElement.lang || undefined, options);
    } catch (error) {
      return date.toLocaleDateString(undefined, options);
    }
  };

  const initDelivery = (root) => {
    root.querySelectorAll('[data-sk-delivery]').forEach((line) => {
      const [ship, low, high] = String(line.dataset.skDelivery).split(',').map((value) => Math.round(Number(value)));
      if (!(ship > 0 && low > 0 && high > 0)) return;
      const today = new Date();
      const dates = {
        from: addBusinessDays(today, ship + Math.min(low, high)),
        to: addBusinessDays(today, ship + Math.max(low, high)),
      };
      line.querySelectorAll('[data-sk-date]').forEach((slot) => {
        const date = dates[slot.dataset.skDate];
        if (date) slot.textContent = dayLabel(date);
      });
      line.removeAttribute('data-sk-pending');
    });
  };

  const strings = readJson(document.querySelector('script[data-sk-strings]')) || {};

  const initMarquees = (root) => {
    root.querySelectorAll('.sk-marquee').forEach((marquee) => {
      if (marquee.dataset.skReady) return;
      marquee.dataset.skReady = 'true';
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'sk-marquee__toggle';
      toggle.textContent = strings.pauseMotion || 'Pause';
      toggle.setAttribute('aria-pressed', 'false');
      marquee.prepend(toggle);
      const setPaused = (paused) => {
        marquee.classList.toggle('is-paused', paused);
        toggle.setAttribute('aria-pressed', String(paused));
      };
      marquee.addEventListener('click', () => setPaused(!marquee.classList.contains('is-paused')));
    });
  };

  /* Countdowns to the end time in the theme settings. The end is a fixed moment in the markup, the same for
     every visitor; the time updates when the minute changes, and at zero the line is removed or shows the
     text set for after the end. */
  let countdownTimer = 0;

  const countdownText = (left) => {
    const total = Math.ceil(left / 60000);
    const days = Math.floor(total / 1440);
    const hours = Math.floor((total % 1440) / 60);
    const parts = [`${total % 60}m`];
    if (days || hours) parts.unshift(`${hours}h`);
    if (days) parts.unshift(`${days}d`);
    return parts.join(' ');
  };

  const initCountdowns = () => {
    clearTimeout(countdownTimer);
    const now = Date.now();
    let next = 0;
    document.querySelectorAll('[data-sk-countdown]').forEach((line) => {
      const left = Number(line.dataset.skCountdown) * 1000 - now;
      if (!(left > 0)) {
        const after = line.dataset.skCountdownAfter;
        const text = line.querySelector('span');
        if (after && text) {
          text.textContent = after;
          line.removeAttribute('data-sk-countdown');
        } else {
          line.remove();
        }
        return;
      }
      const time = line.querySelector('[data-sk-countdown-time]');
      if (time) time.textContent = countdownText(left);
      const wait = left % 60000 || 60000;
      next = next ? Math.min(next, wait) : wait;
    });
    if (next) countdownTimer = setTimeout(initCountdowns, next + 50);
  };

  /* Theme editor: a selected accordion block opens so its content can be seen while editing, and a
     selected block inside a closed drawer (the phone menu's links) shows that drawer without taking
     focus from the editor, until the block is deselected. */
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (target instanceof HTMLDetailsElement) target.open = true;
    const drawer = target instanceof Element && target.closest('dialog.sk-drawer');
    if (!drawer || drawer.open) return;
    drawer.dataset.skPreview = '';
    drawer.setAttribute('open', '');
    syncExpanded(drawer);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const drawer = event.target instanceof Element && event.target.closest('dialog.sk-drawer[data-sk-preview]');
    if (!drawer) return;
    delete drawer.dataset.skPreview;
    drawer.close();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sk-product]').forEach((product) => {
      if (product.skObserver) product.skObserver.disconnect();
      document.documentElement.classList.remove('sk-sticky-on');
    });
  });

  /* Module registry: each module scopes its queries to the root it is given. */
  const modules = [upgradeCartLinks, initOffers, initProducts, initDelivery, initMarquees, initCountdowns];

  const init = (root) => {
    modules.forEach((mod) => mod(root || document));
  };

  window.Satinknit = { cart, openDrawer, closeDrawer, register: (mod) => modules.push(mod) };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
